import { z } from 'zod';
import { RecordIdSchema } from './identifiers';
import {
  BusinessDateSchema,
  InstantSchema,
  LocalTimeSchema,
  businessDateInZone,
  instantInZone,
  type BusinessDate,
  type Instant,
} from './time';

/**
 * Handover and after-sales — Phase 9 (`HAND-READY-001`, `HAND-APPT-001`, `HAND-INSPECT-001`,
 * `HAND-DELIVER-001`, `CS-TICKET-001`).
 *
 * One **handover case** per sold unit (an active or completed contract): a readiness checklist that
 * someone other than its preparer approves, appointments, the defects found at inspection (each with
 * who is responsible, a deadline and its resolution), and the delivery itself — signed handover paper,
 * keys and meter readings — after which the case is closed as delivered. Nothing about keys, meters or
 * defect categories is assumed: they are what the team records.
 *
 * After-sales **tickets** (complaints, maintenance) carry a priority; a response deadline exists only
 * where the client configured service-level hours for that priority (setting `aftersales.slaHours`,
 * `BD-69`); overdue open tickets escalate to the assignee's manager by notification. Satisfaction is the
 * customer's own score, recorded once at closure.
 */

export const HANDOVER_CASE_STATES = [
  'preparing',
  'ready',
  'scheduled',
  'inspected',
  'delivered',
  'cancelled',
] as const;
export const HandoverCaseStateSchema = z.enum(HANDOVER_CASE_STATES);
export type HandoverCaseState = z.infer<typeof HandoverCaseStateSchema>;

export const DEFECT_STATES = ['open', 'resolved', 'waived'] as const;
export const DEFECT_RESPONSIBLE = ['contractor', 'developer', 'customer'] as const;
export const APPOINTMENT_KINDS = ['inspection', 'handover'] as const;
export const APPOINTMENT_STATES = ['planned', 'held', 'missed', 'cancelled'] as const;

export const ReadinessItemSchema = z.strictObject({
  itemId: z.string(),
  title: z.string(),
  done: z.boolean(),
  doneByAccountId: z.string().optional(),
  doneAt: InstantSchema.optional(),
});
export const AppointmentSchema = z.strictObject({
  appointmentId: z.string(),
  kind: z.enum(APPOINTMENT_KINDS),
  /** Instant in UTC; shown in the organization's timezone. */
  at: InstantSchema,
  place: z.string().optional(),
  state: z.enum(APPOINTMENT_STATES),
  note: z.string().optional(),
  recordedByAccountId: z.string(),
});
export const DefectSchema = z.strictObject({
  defectId: z.string(),
  description: z.string(),
  location: z.string().optional(),
  responsible: z.enum(DEFECT_RESPONSIBLE),
  vendorId: z.string().optional(),
  dueOn: BusinessDateSchema.optional(),
  state: z.enum(DEFECT_STATES),
  resolution: z.string().optional(),
  resolvedByAccountId: z.string().optional(),
  resolvedAt: InstantSchema.optional(),
  recordedAt: InstantSchema,
});
export const MeterReadingSchema = z.strictObject({
  meter: z.string(),
  reading: z.string(),
});

export const HandoverCaseSchema = z.strictObject({
  caseId: RecordIdSchema,
  number: z.string(),
  contractId: RecordIdSchema,
  contractNumber: z.string(),
  unitId: RecordIdSchema,
  unitCode: z.string(),
  customerId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema,
  state: HandoverCaseStateSchema,
  readiness: z.array(ReadinessItemSchema),
  readyApprovedByAccountId: z.string().optional(),
  appointments: z.array(AppointmentSchema),
  defects: z.array(DefectSchema),
  delivery: z
    .strictObject({
      deliveredOn: BusinessDateSchema,
      keys: z.number().int().nonnegative(),
      meters: z.array(MeterReadingSchema),
      note: z.string().optional(),
      deliveredByAccountId: z.string(),
    })
    .optional(),
  cancelReason: z.string().optional(),
  ownerAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type HandoverCase = z.infer<typeof HandoverCaseSchema>;

export const OpenHandoverSchema = z.strictObject({
  contractId: RecordIdSchema,
  idempotencyKey: z.string().trim().min(8).max(200),
});
export const HandoverQuerySchema = z.strictObject({
  projectId: RecordIdSchema.optional(),
  state: HandoverCaseStateSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type HandoverQuery = z.infer<typeof HandoverQuerySchema>;

const Version = { expectedVersion: z.number().int().positive() };
export const ReadinessChangeSchema = z
  .strictObject({
    add: z.string().trim().min(2).max(200).optional(),
    complete: z.string().min(1).max(60).optional(),
    ...Version,
  })
  .refine((value) => !!value.add !== !!value.complete, { message: 'ONE_CHANGE', path: ['add'] });
export const ApproveReadinessSchema = z.strictObject({ ...Version });
export const ScheduleSchema = z.strictObject({
  kind: z.enum(APPOINTMENT_KINDS),
  at: InstantSchema,
  place: z.string().trim().min(2).max(200).optional(),
  ...Version,
});
export const AppointmentOutcomeSchema = z.strictObject({
  appointmentId: z.string().min(1).max(60),
  state: z.enum(['held', 'missed', 'cancelled']),
  note: z.string().trim().min(2).max(500).optional(),
  ...Version,
});
export const RecordDefectSchema = z.strictObject({
  description: z.string().trim().min(3).max(500),
  location: z.string().trim().min(2).max(120).optional(),
  responsible: z.enum(DEFECT_RESPONSIBLE),
  vendorId: RecordIdSchema.optional(),
  dueOn: BusinessDateSchema.optional(),
  ...Version,
});
export const ResolveDefectSchema = z.strictObject({
  defectId: z.string().min(1).max(60),
  state: z.enum(['resolved', 'waived']),
  resolution: z.string().trim().min(3).max(500),
  ...Version,
});
export const DeliverSchema = z.strictObject({
  deliveredOn: BusinessDateSchema,
  keys: z.number().int().min(0).max(50),
  meters: z
    .array(
      z.strictObject({
        meter: z.string().trim().min(1).max(60),
        reading: z.string().trim().min(1).max(30),
      }),
    )
    .max(10)
    .default([]),
  note: z.string().trim().min(2).max(500).optional(),
  ...Version,
});
export const CancelHandoverSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  ...Version,
});

/* ---------------------------------------------------------------- tickets */

export const TICKET_KINDS = ['complaint', 'maintenance', 'inquiry'] as const;
export const TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export const TICKET_STATES = ['open', 'inProgress', 'resolved', 'closed', 'cancelled'] as const;

/** Response hours per priority (`BD-69`); null = not configured, no deadline is set. */
export const SlaHoursSchema = z
  .strictObject(
    Object.fromEntries(
      TICKET_PRIORITIES.map((priority) => [priority, z.number().int().min(1).max(2000)]),
    ) as Record<(typeof TICKET_PRIORITIES)[number], z.ZodNumber>,
  )
  .nullable();
export type SlaHours = z.infer<typeof SlaHoursSchema>;

/**
 * The after-sales working calendar (BD-69): response hours count only inside working hours on working
 * days, in the calendar's time zone. Not configured: hours are counted round the clock.
 */
export const WorkingCalendarSchema = z
  .strictObject({
    timeZone: z.string().refine(
      (zone) => {
        try {
          new Intl.DateTimeFormat('en', { timeZone: zone });
          return true;
        } catch {
          return false;
        }
      },
      { message: 'TIME_ZONE_INVALID' },
    ),
    /** 0 = Sunday … 6 = Saturday. */
    workingDays: z.array(z.number().int().min(0).max(6)).min(1).max(7),
    start: LocalTimeSchema,
    end: LocalTimeSchema,
  })
  .refine((value) => value.end > value.start, { message: 'END_BEFORE_START', path: ['end'] })
  .nullable();
export type WorkingCalendar = z.infer<typeof WorkingCalendarSchema>;

/** `from` plus `hours` of working time under a calendar (round the clock without one). */
export function addWorkingHours(from: Date, hours: number, calendar: WorkingCalendar): Date {
  if (!calendar) return new Date(from.getTime() + hours * 3_600_000);
  let remaining = hours * 3_600_000;
  let cursor = from.getTime();
  for (let guard = 0; guard < 800 && remaining > 0; guard += 1) {
    const date = businessDateInZone(new Date(cursor).toISOString() as Instant, calendar.timeZone);
    const weekday = new Date(`${date}T12:00:00Z`).getUTCDay();
    const open = Date.parse(instantInZone(date, calendar.start, calendar.timeZone));
    const close = Date.parse(instantInZone(date, calendar.end, calendar.timeZone));
    if (calendar.workingDays.includes(weekday) && cursor < close) {
      const begin = Math.max(cursor, open);
      const available = close - begin;
      if (remaining <= available) return new Date(begin + remaining);
      remaining -= available;
    }
    // Next day at midnight, in the calendar's zone.
    const next = new Date(`${date}T12:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    cursor = Date.parse(
      instantInZone(next.toISOString().slice(0, 10) as BusinessDate, '00:00', calendar.timeZone),
    );
  }
  return new Date(cursor + remaining);
}

export const TicketSchema = z.strictObject({
  ticketId: RecordIdSchema,
  number: z.string(),
  kind: z.enum(TICKET_KINDS),
  priority: z.enum(TICKET_PRIORITIES),
  subject: z.string(),
  description: z.string(),
  customerId: RecordIdSchema,
  contractId: RecordIdSchema.optional(),
  unitId: RecordIdSchema.optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  assigneeAccountId: z.string().optional(),
  state: z.enum(TICKET_STATES),
  dueAt: InstantSchema.optional(),
  escalatedAt: InstantSchema.optional(),
  resolution: z.string().optional(),
  satisfaction: z.number().int().min(1).max(5).optional(),
  history: z.array(
    z.strictObject({
      at: InstantSchema,
      byAccountId: z.string(),
      from: z.string().optional(),
      to: z.string(),
      note: z.string().optional(),
    }),
  ),
  createdByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Ticket = z.infer<typeof TicketSchema>;

export const CreateTicketSchema = z.strictObject({
  kind: z.enum(TICKET_KINDS),
  priority: z.enum(TICKET_PRIORITIES),
  subject: z.string().trim().min(3).max(160),
  description: z.string().trim().min(3).max(2000),
  customerId: RecordIdSchema,
  contractId: RecordIdSchema.optional(),
  assigneeAccountId: z.string().min(1).max(200).optional(),
  idempotencyKey: z.string().trim().min(8).max(200),
});
export const TicketActionSchema = z.strictObject({
  action: z.enum(['start', 'resolve', 'close', 'cancel', 'reopen', 'assign']),
  note: z.string().trim().min(2).max(1000).optional(),
  assigneeAccountId: z.string().min(1).max(200).optional(),
  satisfaction: z.number().int().min(1).max(5).optional(),
  ...Version,
});
export const TicketQuerySchema = z.strictObject({
  state: z.enum(TICKET_STATES).optional(),
  priority: z.enum(TICKET_PRIORITIES).optional(),
  customerId: RecordIdSchema.optional(),
  mine: z.enum(['true', 'false']).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type TicketQuery = z.infer<typeof TicketQuerySchema>;

export const HANDOVER_AUDIT_ACTIONS = {
  opened: 'handover.case.opened',
  changed: 'handover.case.changed',
  ready: 'handover.case.ready',
  delivered: 'handover.case.delivered',
  cancelled: 'handover.case.cancelled',
  ticketCreated: 'aftersales.ticket.created',
  ticketChanged: 'aftersales.ticket.changed',
} as const;
